import type { RawDraftContentState } from 'draft-js'

/**
 * The draft-free half of the inline-image helpers: reducers import these, and pulling them
 * from editorImageHelpers put the whole draft-js editor on every cold start.
 */
export const IMAGE_ENTITY_TYPE = 'IMAGE'

export interface IImageEntityData {
  // Persisted reference — the S3 key for the image bytes. The only field that
  // survives a reload (the server hydrates `src` from it on read).
  key?: string
  // Render source filled by the server on read (base64 data URL from S3). Not
  // persisted.
  src?: string
  // Session-local object URL shown instantly on insert (synchronously), so the
  // image appears and the editor keeps focus without waiting on the upload. Not
  // persisted.
  localSrc?: string
  fileName?: string
}

export const hasInlineImage = (content: RawDraftContentState | null | undefined): boolean => {
  if (!content?.entityMap) {
    return false
  }
  return Object.values(content.entityMap).some((entity) => entity.type === IMAGE_ENTITY_TYPE)
}

/** An inline image and the key of the block it sits in, which tells two copies of it apart. */
export interface IInlineImage {
  blockKey: string
  image: IImageEntityData
}

/** The content's inline images, in the order they appear. */
export const listInlineImages = (
  content: RawDraftContentState | null | undefined,
): IInlineImage[] => {
  if (!content || !hasInlineImage(content)) {
    return []
  }
  return content.blocks.flatMap((block) => {
    const entityKey = block.entityRanges[0]?.key
    const entity = entityKey === undefined ? undefined : content.entityMap[entityKey]
    return entity?.type === IMAGE_ENTITY_TYPE ? [{ blockKey: block.key, image: entity.data }] : []
  })
}

// Keeps only the persisted { key, fileName } on each image entity, so a save
// doesn't upload the hydrated base64 `src` back to the server.
export const stripImageRenderData = (content: RawDraftContentState): RawDraftContentState => {
  if (!hasInlineImage(content)) {
    return content
  }
  const entityMap = Object.fromEntries(
    Object.entries(content.entityMap).map(([id, entity]) => {
      if (entity.type !== IMAGE_ENTITY_TYPE) {
        return [id, entity]
      }
      const { key, fileName } = entity.data as IImageEntityData
      return [id, { ...entity, data: { key, fileName } }]
    }),
  )
  return { ...content, entityMap }
}

/**
 * `content` with each image that arrived without render data borrowing it from
 * the same key in `rendered`. A save echo hydrates only the images that save
 * added; the ones already on screen keep the bytes they were loaded with.
 */
export const fillImageRenderData = (
  content: RawDraftContentState,
  rendered: RawDraftContentState,
): RawDraftContentState => {
  if (!hasInlineImage(content)) {
    return content
  }
  const renderedByKey = new Map(
    Object.values(rendered.entityMap ?? {})
      .filter((entity) => entity.type === IMAGE_ENTITY_TYPE)
      .map((entity) => entity.data as IImageEntityData)
      .filter((data) => data.key)
      .map((data) => [data.key, data]),
  )
  const entityMap = Object.fromEntries(
    Object.entries(content.entityMap).map(([id, entity]) => {
      const data = entity.data as IImageEntityData
      const known = data.key ? renderedByKey.get(data.key) : undefined
      if (entity.type !== IMAGE_ENTITY_TYPE || data.src || !known) {
        return [id, entity]
      }
      const renderData = {
        ...(known.src && { src: known.src }),
        ...(known.localSrc && { localSrc: known.localSrc }),
      }
      return [id, { ...entity, data: { ...data, ...renderData } }]
    }),
  )
  return { ...content, entityMap }
}
